export interface FieldSection {
  id: string;
  name: string;
  description: string;
  icon: string;
  tags: string[];
  priority: number;
  defaultExpanded?: boolean;
}

export const FIELD_SECTIONS: FieldSection[] = [
  {
    id: 'identity',
    name: 'Identity',
    description: 'Name, title, contact, and employee identification',
    icon: 'user',
    tags: ['identity', 'contact'],
    priority: 100,
    defaultExpanded: true,
  },
  {
    id: 'role',
    name: 'Role & Position',
    description: 'Job architecture, level, employment type',
    icon: 'briefcase',
    tags: ['role', 'employment'],
    priority: 90,
  },
  {
    id: 'organization',
    name: 'Organization',
    description: 'Division, business unit, cost center, location',
    icon: 'building',
    tags: ['organization', 'location'],
    priority: 80,
  },
  {
    id: 'talent',
    name: 'Talent Context',
    description: 'Performance, potential, and talent assessment data',
    icon: 'star',
    tags: ['talent', 'performance', 'potential'],
    priority: 75,
  },
  {
    id: 'succession',
    name: 'Succession',
    description: 'Succession planning, career path, development',
    icon: 'arrow-up-right',
    tags: ['succession', 'career', 'development'],
    priority: 70,
  },
  {
    id: 'compensation',
    name: 'Cost & Compensation',
    description: 'Salary, pay band, total compensation',
    icon: 'dollar-sign',
    tags: ['compensation'],
    priority: 65,
  },
  {
    id: 'skills',
    name: 'Skills & Capabilities',
    description: 'Skills, certifications, education, competencies',
    icon: 'award',
    tags: ['skills', 'education', 'competency'],
    priority: 60,
  },
  {
    id: 'workforce',
    name: 'Workforce Planning',
    description: 'Tenure, retirement, contracts, mobility, compliance',
    icon: 'calendar',
    tags: ['workforce', 'timeline', 'compliance'],
    priority: 55,
  },
  {
    id: 'demographics',
    name: 'Demographics',
    description: 'Diversity, age, nationality, veteran status',
    icon: 'users',
    tags: ['demographics', 'dei'],
    priority: 50,
  },
  {
    id: 'engagement',
    name: 'Engagement',
    description: 'Engagement scores, satisfaction, retention signals',
    icon: 'heart',
    tags: ['engagement', 'retention'],
    priority: 45,
  },
  {
    id: 'notes',
    name: 'Notes',
    description: 'Additional notes and documentation',
    icon: 'file-text',
    tags: ['notes'],
    priority: 10,
  },
];

export interface UseCaseSectionConfig {
  useCaseId: string;
  visibleSections: string[];
  expandedSections: string[];
}

export const USE_CASE_SECTION_CONFIGS: UseCaseSectionConfig[] = [
  {
    useCaseId: 'reorg',
    visibleSections: ['identity', 'role', 'organization', 'notes'],
    expandedSections: ['identity', 'role'],
  },
  {
    useCaseId: 'new-team',
    visibleSections: ['identity', 'role', 'skills', 'notes'],
    expandedSections: ['identity', 'role', 'skills'],
  },
  {
    useCaseId: 'succession',
    visibleSections: ['identity', 'talent', 'succession', 'notes'],
    expandedSections: ['identity', 'talent', 'succession'],
  },
  {
    useCaseId: 'workforce-plan',
    visibleSections: ['identity', 'organization', 'role', 'workforce', 'compensation', 'notes'],
    expandedSections: ['identity', 'workforce'],
  },
  {
    useCaseId: 'merger',
    visibleSections: ['identity', 'organization', 'role', 'compensation', 'notes'],
    expandedSections: ['identity', 'organization'],
  },
  {
    useCaseId: 'team-assembly',
    visibleSections: ['identity', 'skills', 'notes'],
    expandedSections: ['identity', 'skills'],
  },
  {
    useCaseId: 'full',
    visibleSections: [
      'identity', 'role', 'organization', 'talent', 'succession',
      'compensation', 'skills', 'workforce', 'demographics', 'engagement', 'notes',
    ],
    expandedSections: ['identity'],
  },
  {
    useCaseId: 'default',
    visibleSections: ['identity', 'role', 'organization', 'notes'],
    expandedSections: ['identity'],
  },
];

export function getSectionConfigForUseCase(useCaseId: string | undefined): UseCaseSectionConfig {
  if (!useCaseId) {
    return USE_CASE_SECTION_CONFIGS.find(config => config.useCaseId === 'default')!;
  }
  const config = USE_CASE_SECTION_CONFIGS.find(config => config.useCaseId === useCaseId);
  return config || USE_CASE_SECTION_CONFIGS.find(config => config.useCaseId === 'default')!;
}

export function getFieldSection(fieldTags: string[] | { id: string }[]): FieldSection | undefined {
  if (!fieldTags || fieldTags.length === 0) return undefined;
  const firstTag = fieldTags[0];
  const primaryTag = typeof firstTag === 'string' ? firstTag : firstTag.id;
  const sortedSections = [...FIELD_SECTIONS].sort((a, b) => b.priority - a.priority);
  return sortedSections.find(section => section.tags.includes(primaryTag));
}

export function groupFieldsBySection(fields: { tags: string[] | { id: string }[] }[]): Map<string, typeof fields> {
  const grouped = new Map<string, typeof fields>();
  for (const field of fields) {
    const section = getFieldSection(field.tags);
    if (section) {
      const sectionFields = grouped.get(section.id) || [];
      sectionFields.push(field);
      grouped.set(section.id, sectionFields);
    }
  }
  return grouped;
}
